import { useState, useEffect } from 'react';
import { getRequestAuthHeaders } from '@/lib/requestAuth';
import type { ModuleStatus } from '@shared/schema';
import { useSubject } from '@/contexts/SubjectContext';

export function useModuleStatus(moduleName: string) {
  const { currentSubject } = useSubject();
  const [status, setStatus] = useState<'active' | 'coming_soon' | 'maintenance' | 'hidden' | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        if (!currentSubject) return;
        const headers = await getRequestAuthHeaders();
        const res = await fetch(`/api/module-status?subject=${currentSubject.id}&_t=${Date.now()}`, { headers });

        const contentType = res.headers.get('content-type') || '';
        if (!res.ok || !contentType.includes('application/json')) {
          // Server returned HTML (SPA fallback) or an error — treat as active
          setStatus('active');
          return;
        }

        const data: ModuleStatus[] = await res.json();
        const moduleInfo = data.find(m => m.moduleName === moduleName);
        
        setStatus(moduleInfo?.status || 'active');
      } catch (err: any) {
        console.error(err);
        setError(err.message);
        setStatus('active'); // fallback to active if request fails so we don't break the app
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [moduleName, currentSubject]);

  return { status, loading, error };
}

export function useAllModuleStatuses() {
  const { currentSubject } = useSubject();
  const [statuses, setStatuses] = useState<Record<string, 'active' | 'coming_soon' | 'maintenance' | 'hidden'>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        if (!currentSubject) return;
        const headers = await getRequestAuthHeaders();
        const res = await fetch(`/api/module-status?subject=${currentSubject.id}&_t=${Date.now()}`, { headers });

        const contentType = res.headers.get('content-type') || '';
        if (!res.ok || !contentType.includes('application/json')) {
          setStatuses({});
          return;
        }

        const data: ModuleStatus[] = await res.json();
        const statusMap: Record<string, 'active' | 'coming_soon' | 'maintenance' | 'hidden'> = {};
        data.forEach(m => { statusMap[m.moduleName] = m.status; });
        setStatuses(statusMap);
      } catch (err: any) {
        console.error(err);
        setStatuses({});
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [currentSubject]);

  return { statuses, loading };
}
